import {apiRetrieveImproved} from "@/api.ts";
import {football, FootballProfileData} from "@/football/api.ts";
import {ProfileSportTabs, ProfileTabKey, profileTabs} from "@/football/profileTabs.tsx";
import PlayerProfile from "@/profile/PlayerProfile.tsx";
import {useSports} from "@/sports/registry.ts";
import {PlayerDashboard} from "@/types";
import queryKeys from "@/utilities/queryKeys.ts";
import {useQuery} from "@tanstack/react-query";
import {createFileRoute, useParams, useSearch} from '@tanstack/react-router'
import React, {useState} from "react";

export const Route = createFileRoute('/_app/profiles/$player_uid/')({
    component: RouteComponent,
})

function RouteComponent() {
    const params = useParams({strict: false});
    const search = useSearch({strict: false}) as {sport?: string};
    const {data: registry} = useSports();
    // `?sport=football` (Club Players' football rows) opens on the Football tab where there is one.
    const [chosen, setChosen] = useState<ProfileTabKey | null>(search.sport === 'football' ? 'football' : null);

    const profileQuery = useQuery<PlayerDashboard>({
        queryKey: queryKeys.profile(params.player_uid!),
        queryFn:  async () => apiRetrieveImproved('player-profile', {player_uid: params.player_uid}),
        enabled:  !!params.player_uid,
    })

    // Football v2: football is a page of the player's profile beside the one they always had, one
    // tab a sport, opening on the sport they played most recently. Off, or for a player with
    // nothing football about them, the page is exactly as it was.
    const footballOn = !!registry?.multi_sport && !!registry?.football_v2;
    const footballQuery = useQuery<FootballProfileData>({
        queryKey: ['football-profile', params.player_uid],
        queryFn:  () => football.profile(params.player_uid!),
        enabled:  !!params.player_uid && footballOn,
        retry:    false,
    });

    // While football is still being asked about, nothing is chosen: the racquet page's own loading state.
    if (footballOn && footballQuery.isLoading) {
        return <PlayerProfile profile={undefined} />
    }
    const {tabs, initial} = registry ? profileTabs(registry, footballQuery.data) : {tabs: [], initial: 'other' as ProfileTabKey};
    const active = chosen && (tabs.some((t) => t.key === chosen)) ? chosen : initial;

    // One page for every sport: the Football tab is the same profile with football's content.
    // The sport pills sit under the page's header.
    const sportTabs = <ProfileSportTabs tabs={tabs} active={active} onChange={setChosen} />;
    return <PlayerProfile profile={profileQuery.data} sportTabs={sportTabs}
                          football={active === 'football' ? footballQuery.data : undefined} />;
}
